/**
 * The web half of the camera-roll backup — which is almost none of it.
 *
 * Everything that does the work is Swift, in `ios/App/App/GallerySync.swift`,
 * and that is the opposite of this codebase's usual instinct: logic belongs in
 * `src/lib` where it can be tested without a browser. The reason it cannot be
 * here is `BGProcessingTask`. iOS suspends the WebView within seconds of the
 * app leaving the screen, so a JavaScript loop cannot be the background path,
 * and a foreground engine here plus a background engine there would be two
 * local indexes, two pacing rules and two ways to be half finished.
 *
 * So the division is: this file owns consent, the permission conversation and
 * saying how far along it is; Swift owns enumeration, thumbnails, the index of
 * what has already gone, pacing and the upload. See docs/GALLERY.md.
 */
import { isNative } from '../api/origin.ts';

/**
 * What iOS says about the photo library. `limited` is the one that matters:
 * both platforms now make «Выбрать фото» the prominent button, and a backup
 * that quietly holds four photographs looks exactly like one that is working.
 */
export type GalleryPermission =
  | 'notDetermined'
  | 'denied'
  | 'restricted'
  | 'limited'
  | 'full'
  /** Not a native build, or a platform with no plugin yet. */
  | 'unavailable';

export interface GalleryStatus {
  permission: GalleryPermission;
  /**
   * Whether this app was ever switched on here — kept in UserDefaults, so it
   * is the one piece of this status that survives the launch it was set in.
   * Everything else below is this launch's engine, which starts empty.
   *
   * Absent from a native half older than useGallerySync.ts, which is why the
   * autostart treats it as false rather than as consent it cannot see.
   */
  enabled: boolean;
  /** Photographs on the phone. 0 until the first enumeration finishes. */
  total: number;
  /** Of those, how many the server has confirmed. */
  done: number;
  /**
   * Of those, how many the current pass could not read at all — an original
   * that lives only in iCloud, most of them. Not a failure and not progress:
   * `done + skipped === total` is a library that has sent everything it can,
   * which is a different thing from `done === total` and reads on screen as a
   * different sentence. See `unreadableRetry` in GallerySync.swift for how long
   * one of these is left alone before the phone tries it again.
   *
   * Optional because a native half older than this does not count them, and a
   * missing count is not a zero: it is a phone that has never been asked.
   */
  skipped?: number;
  /**
   * The id the engine's UserDefaults hold — its own persisted copy of what
   * `configure` last handed over. Reported back because the two stores fail
   * differently: iOS has been seen wiping the WebView's storage wholesale
   * (localStorage, session token and all) while UserDefaults sat untouched, and
   * after such a wipe this copy is the only surviving name the server knows
   * this install by. See adoptGalleryDeviceId. Optional because a native half
   * older than this does not report it.
   */
  deviceId?: string;
  running: boolean;
  /** Backing off — the link is too slow, or the server is refusing. */
  paused: boolean;
  /** When the last batch landed, from the device's clock. 0 for never. */
  lastRunAt: number;
}

/** One thing the engine could not do, on its way to reportClientError. */
export interface GalleryFailure {
  detail: string;
  message: string;
  status: number | null;
}

/** One thing the engine did, on its way to the panel. See GallerySync.swift. */
export interface GalleryEvent {
  /** Milliseconds since the epoch, from the device's clock. */
  at: number;
  text: string;
}

/** What ios/App/App/GalleryPlugin.swift exposes. Kept in step by hand. */
export interface GalleryPlugin {
  permission(): Promise<{ permission: GalleryPermission }>;
  requestPermission(): Promise<{ permission: GalleryPermission }>;
  /** Where to upload and how to prove who we are; see configure() in Swift. */
  configure(options: {
    origin: string;
    token: string;
    deviceId: string;
    userId: string;
  }): Promise<void>;
  /**
   * `source` says who asked — 'manual' for the button, 'auto' for the resume on
   * an app open — and is stamped on every line that run writes to
   * `gallery_sync_log`. Swift cannot work it out for itself: both are this same
   * call. Optional, because a native half older than this treats it as absent
   * and defaults to 'manual'.
   */
  start(options?: { source?: string }): Promise<void>;
  stop(): Promise<void>;
  status(): Promise<GalleryStatus>;
  /** Up to twenty failures, cleared as they are read. */
  drainErrors(): Promise<{ errors: GalleryFailure[] }>;
  /** The engine's account of itself since the last ask, cleared as it is read. */
  drainEvents(): Promise<{ events: GalleryEvent[] }>;
  /** iOS Settings, for the app's own entry — the only way out of `limited`. */
  openSettings(): Promise<void>;
}

/**
 * The version of the consent text on screen right now.
 *
 * Bumped whenever the wording below the heading changes in a way that changes
 * what is being agreed to. It is stored with the consent server-side, because
 * what somebody agreed to is the text they were shown, and a row that records
 * only "yes" does not record the promise that was made.
 */
export const CONSENT_VERSION = '1';

const DEVICE_KEY = 'chat:gallery-device';

/**
 * This installation's id, minted here and kept in localStorage.
 *
 * On the phone that store is application data rather than the script-writable
 * storage Safari evicts (see api/origin.ts), so it survives properly — and a
 * reinstall that loses it is exactly the case the server's `have` handshake is
 * there for, so losing it costs a few hundred round trips rather than twenty
 * thousand uploads.
 */
export function galleryDeviceId(): string {
  const existing = localStorage.getItem(DEVICE_KEY);
  if (existing) return existing;
  // randomUUID needs a secure context; capacitor://localhost is one, but a
  // build previewed over plain HTTP on a LAN address is not.
  const minted =
    typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
  localStorage.setItem(DEVICE_KEY, minted);
  return minted;
}

/**
 * A fresh identity for this install, replacing the stored one.
 *
 * For the one answer registration gives about the stored id that cannot be
 * argued with: 409, «привязано к другому аккаунту». The id is minted per
 * install while accounts come and go on a phone, so the second account to
 * agree to the backup arrives carrying the first one's key. Their photographs
 * stay theirs on the server; this account starts over as the new device it
 * effectively is, and the `have` handshake keeps starting over cheap.
 */
export function remintGalleryDeviceId(): string {
  localStorage.removeItem(DEVICE_KEY);
  localStorage.removeItem(REGISTERED_KEY);
  return galleryDeviceId();
}

/**
 * The stored id, or the engine's surviving copy of it — never a fresh mint
 * while either store still remembers.
 *
 * The two copies exist because the two runtimes do (see configure in
 * GallerySync.swift), and they fail separately: iOS wiping the WebView's
 * storage loses this side's id while UserDefaults keep the other. Minting a
 * new id in that state is what a bare galleryDeviceId() would do, and it is
 * expensive twice over — the new id is not in gallery_devices, so every batch
 * answers 404 «Устройство не зарегистрировано», and even re-registered it is a
 * new device to the server, so the resume handshake forgets everything this
 * phone ever sent. Taking the engine's copy back instead makes the wipe cost
 * nothing at all.
 *
 * The shape check mirrors the server's: an engine that was never configured
 * reports an empty string, which must not become the stored id.
 */
export function adoptGalleryDeviceId(engineId: string | undefined): string {
  const existing = localStorage.getItem(DEVICE_KEY);
  if (existing) return existing;
  if (engineId && /^[0-9a-f-]{8,64}$/i.test(engineId)) {
    localStorage.setItem(DEVICE_KEY, engineId);
    return engineId;
  }
  return galleryDeviceId();
}

const REGISTERED_KEY = 'chat:gallery-device-registered';

/**
 * Whether POST /api/gallery/device has confirmed this pair, so the autostart
 * knows to re-confirm and the ordinary launch is not taxed a request.
 *
 * Keyed by account *and* id: a different account on the same phone must
 * re-register (and be told 409), and a reminted id must too. Kept in the same
 * localStorage as the id itself, deliberately — the wipe that loses one loses
 * both, which is exactly the state in which re-registering is the repair.
 */
export function deviceRegistrationConfirmed(userId: string, deviceId: string): boolean {
  return localStorage.getItem(REGISTERED_KEY) === `${userId}:${deviceId}`;
}

export function confirmDeviceRegistration(userId: string, deviceId: string): void {
  localStorage.setItem(REGISTERED_KEY, `${userId}:${deviceId}`);
}

/**
 * The plugin, or null off the phone.
 *
 * Taken straight out of `window.Capacitor.Plugins`, which the native half writes
 * into the page at document start: one `WKUserScript` per registered plugin,
 * generated by `JSExport.exportJS`. The methods on that object are the same
 * `Capacitor.nativePromise` calls `registerPlugin()` would eventually make, so
 * the short way round costs nothing.
 *
 * It used to be `import('@capacitor/core').then(registerPlugin)`, loaded on
 * demand so the web bundle would not carry a plugin it can never use — and
 * inside the shell **that import never settled**. Not rejected: settled never,
 * which is the failure with no symptoms of its own. Every caller here awaits it
 * first, so `poll()` never reached a bridge call and the screen never learned it
 * was blind, and `enable()` never reached one either — the button that promises
 * a system dialog stayed greyed out with `withTimeout` below it untouched, since
 * that guards the call and there was never a call.
 *
 * So this is synchronous on purpose rather than as a tidy-up: there is no await
 * left in front of the bridge for anything to hang on. The web build keeps its
 * dead-code guarantee too, and more cheaply than the dynamic import bought it —
 * @capacitor/core is no longer imported here at all.
 */
export function galleryPlugin(): GalleryPlugin | null {
  if (!isNative) return null;
  const injected = (window as unknown as { Capacitor?: { Plugins?: Record<string, unknown> } })
    .Capacitor?.Plugins?.Gallery;
  return (injected as GalleryPlugin | undefined) ?? null;
}

/**
 * A plugin call that is allowed to never answer, made to answer anyway.
 *
 * This is not defensive programming, it is a documented failure mode of the
 * bridge: when Capacitor cannot find the plugin a call is addressed to, it logs
 * one line and returns — see `handleJSCall` in CapacitorBridge.swift. It does
 * not reject. So the promise on this side settles never, and a screen awaiting
 * it waits for the lifetime of the app with its button greyed out and nothing
 * to say. The case is not exotic either: it is exactly what an app whose web
 * bundle is newer than its native half looks like.
 *
 * Rejecting after a while turns that into a sentence somebody can act on.
 */
export function withTimeout<T>(work: Promise<T>, ms: number, what: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`Нативная часть приложения не отвечает (${what})`)),
      ms,
    );
    work.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error instanceof Error ? error : new Error(String(error)));
      },
    );
  });
}

/**
 * How long a plugin call may take before it is called silence.
 *
 * Generous, because one of these calls puts a system dialog on screen and waits
 * for a person to read it. A minute is long enough for that and short enough
 * that a broken build is diagnosed while somebody is still looking at it.
 */
export const PLUGIN_TIMEOUT_MS = 60_000;

/** Nothing has happened yet — what the screen shows before the first poll. */
export const IDLE_STATUS: GalleryStatus = {
  permission: 'unavailable',
  enabled: false,
  total: 0,
  done: 0,
  running: false,
  paused: false,
  lastRunAt: 0,
};

/**
 * Whether an app that has just been opened should set a run going.
 *
 * Three conditions and each is a different «no»: nobody ever agreed on this
 * phone, the access is short of full and the consent screen has a warning about
 * it, or a run is already going and a second start would be noise. Anything else
 * is a library that has more to send and an engine sitting idle, which is the
 * state this app spent every launch after the first one in — see
 * useGallerySync.ts for the whole of that story.
 */
export function shouldStartSync(status: GalleryStatus): boolean {
  return status.enabled && status.permission === 'full' && !status.running;
}

/** «1 240» — a count of photographs is long enough to need the grouping. */
export function groupDigits(value: number): string {
  return String(Math.max(0, Math.trunc(value))).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

/**
 * The engine's state as one line for the log panel — and only for it.
 *
 * There used to be `progressLabel` here, printed under the heading: «1 240 из
 * 20 500, осталось 19 260», with variants for paused, stopped and finished.
 * Above the panel it was the same numbers twice, and its first case —
 * «Считаем фотографии…» — is what somebody saw for as long as the first
 * enumeration took, which reads as an app that is stuck rather than one that is
 * working. The screen above says «Включено» and nothing else now.
 *
 * docs/GALLERY.md still asks for what is left to be reported rather than
 * implied finished, and it still is: by the engine, in the panel, in the lines
 * it writes when it actually sends something. This is the line that opens that
 * account, so that a library which is already finished — no events, nothing to
 * send — still says how much of it there is.
 */
export function statusLine(status: GalleryStatus): string {
  const skipped = status.skipped ?? 0;
  // «411 из 542, 131 недоступно» rather than «411 из 542», which is the same
  // line a sync that has stalled writes. The clause is only ever added when
  // there is something to add: a library the phone can read whole has nothing
  // to explain and should not be given a caveat to read.
  const counts =
    `${groupDigits(status.done)} из ${groupDigits(status.total)}` +
    (skipped > 0 ? `, ${groupDigits(skipped)} недоступно` : '');
  if (status.paused) return `${counts}, ждём хорошую сеть`;
  return `${counts}, ${status.running ? 'идёт загрузка' : 'проход не идёт'}`;
}
